import {ChangeDetectionStrategy, Component, OnDestroy, signal, inject, input, output, forwardRef, Input} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {DateAdapter, MAT_DATE_FORMATS, provideNativeDateAdapter} from '@angular/material/core';
import {MatCalendar, MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import { Overlay } from "@angular/cdk/overlay";
import {Subject} from 'rxjs';
import {startWith, takeUntil} from 'rxjs/operators';
import { CustomDatePickerOverlay } from './naps-custom-datepicker-overlay';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';


@Component({
  selector: 'app-naps-date-picker',
  templateUrl: './naps-date-picker.component.html',
  styleUrl: './naps-date-picker.component.scss',
  providers: [
    provideNativeDateAdapter(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NapsDatePickerComponent),
      multi: true
    }
  ],
  imports: [MatFormFieldModule, MatInputModule, MatDatepickerModule,MatIconModule,ReactiveFormsModule,NgClass],
  viewProviders: [
    {
      provide: Overlay,
      useClass: CustomDatePickerOverlay,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NapsDatePickerComponent implements ControlValueAccessor {

readonly customHeader = NapsCalendarHeader;
customFormControl = input<FormControl>(new FormControl(''));
label = input<string>('naps DatePicker');
placeholder = input<string>('');
isRequired = input<boolean>(false);
hintMessage = input<string>('');
ariaLabel = input<string>('');
disabledField=input<boolean>(false);
dateChanged= output<Date|{year:number;month:number,day:number}>();
napsDateFormat = input<boolean>(false);

internalDate: Date | null = null;

@Input() set minDate(value: any) {
  this._minDate = this.convertToDate(value);
}
get minDate(): any {
  return this._minDate;
}
private _minDate: Date;

@Input() set maxDate(value: any) {
  this._maxDate = this.convertToDate(value);
}
get maxDate(): any {
  return this._maxDate;
}
private _maxDate: Date;



private convertToDate(value: any): Date | null {
  if (!value) return null;

  if (this.napsDateFormat && value.year && value.month && value.day) {
    console.log('naps getting Called');
    return new Date(value.year, value.month - 1, value.day);
  }

  // If already a Date object
  if (value instanceof Date) {
    return value;
  }

  // If ISO string like "2025-05-15"
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    console.log('string getting called');
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  // Attempt fallback parsing
  const fallback = new Date(value);
  return isNaN(fallback.getTime()) ? null : fallback;
}


  // ControlValueAccessor methods
  private onChange: (_: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: any): void {
    const converted = this.convertToDate(value);
    this.internalDate = converted;
    this.customFormControl().setValue(converted, { emitEvent: false });
  }
  
  

  registerOnChange(fn: any): void {
    this.onChange = fn;
    this.customFormControl().valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.customFormControl().disable() : this.customFormControl().enable();
  }

  handleDateChange(event: any) {
    const value = event.value;
    const emittedValue = this.napsDateFormat ? this.getDateStructureFromDate(value) : value;
  
    this.customFormControl().setValue(value);
    this.dateChanged.emit(emittedValue);
    this.onChange(emittedValue);
    this.onTouched();
  }
  
  getDateStructureFromDate(date: Date): { year: number, month: number, day: number } {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };
  }
  
}










/** Custom header component for datepicker. */
@Component({
  selector: 'naps-calendar-header',
  styles: `
    .naps-calendar-header {
      display: flex;
      align-items: center;
      margin-bottom:var(--spacing-s16);
    }

    .naps-calendar-header-label {
      flex: 1;
      height: 1em;
      font-size: var(--font-sm);
      color:var(--color-grey-900);
      font-weight: var(--font-weight-semibold);
      text-align: center;
    }
  `,
  template: `
    <div class="naps-calendar-header">
      <button mat-icon-button (click)="previousClicked('year')">
        <mat-icon>keyboard_double_arrow_left</mat-icon>
      </button>
      <button mat-icon-button (click)="previousClicked('month')">
        <mat-icon>keyboard_arrow_left</mat-icon>
      </button>
      <span class="naps-calendar-header-label">{{periodLabel()}}</span>
      <button mat-icon-button (click)="nextClicked('month')">
        <mat-icon>keyboard_arrow_right</mat-icon>
      </button>
      <button mat-icon-button (click)="nextClicked('year')">
        <mat-icon>keyboard_double_arrow_right</mat-icon>
      </button>
    </div>
  `,
  imports: [MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NapsCalendarHeader<D> implements OnDestroy {
  private _calendar = inject<MatCalendar<D>>(MatCalendar);
  private _dateAdapter = inject<DateAdapter<D>>(DateAdapter);
  private _dateFormats = inject(MAT_DATE_FORMATS);

  private _destroyed = new Subject<void>();

  readonly periodLabel = signal('');

  constructor() {
    this._calendar.stateChanges.pipe(startWith(null), takeUntil(this._destroyed)).subscribe(() => {
      this.periodLabel.set(
        this._dateAdapter
          .format(this._calendar.activeDate, this._dateFormats.display.monthYearLabel)
          .toLocaleUpperCase(),
      );
    });
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  previousClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);
  }

  nextClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, 1);
  }
}

